import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  X, Printer, Download, Send, Loader2, ChevronUp, ChevronDown, Edit2,
  Paperclip, Mail, Phone,
} from "lucide-react";
import api, { formatErr, fileUrl } from "../lib/apiClient";
import { fmtCAD } from "../lib/format";
import { DEFAULT_CUSTOMER_VIEW, mergeCustomerView } from "./SettingsInvoices";

/**
 * SendInvoicePreview — HouseCall Pro style "Send invoice" preview page.
 * Loaded at /invoices/:id/send. The Send button on JobDetail and the
 * Invoices list navigate here instead of firing the send API directly.
 *
 * Left column: editable Details + Attachments + Invoice message + Payment
 *   options + field-visibility checkboxes (which are saved to localStorage so
 *   the preview state persists across visits).
 * Right column: rendered preview of the invoice as the customer will see it.
 * Header: X (cancel), Print, Download PDF, Send.
 */

const LS_KEY = "invoicePreviewPrefs.v1";

// Local module defaults kept as a fallback if the server never returned prefs
// AND localStorage is empty. Sourced from the shared DEFAULT_CUSTOMER_VIEW so we
// don't drift out of sync with the Settings → Invoices → Customer view page.
const DEFAULT_PREFS = DEFAULT_CUSTOMER_VIEW;

function loadLocalOverrides() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

// Merge order: DEFAULT_PREFS < server settings.invoice_customer_view < per-user localStorage overrides.
function mergePrefs(serverPrefs, localOverrides) {
  const base = mergeCustomerView(serverPrefs);
  if (!localOverrides) return base;
  return {
    sections: { ...base.sections, ...(localOverrides.sections || {}) },
    fields: { ...base.fields, ...(localOverrides.fields || {}) },
    viewFormat: localOverrides.viewFormat || base.viewFormat,
  };
}

export default function SendInvoicePreview() {
  const { id } = useParams();
  const nav = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sending, setSending] = useState(false);
  const [prefs, setPrefs] = useState(() => mergePrefs(null, loadLocalOverrides()));

  // Editable draft fields (only affect preview; not persisted server-side yet)
  const [invoiceMessage, setInvoiceMessage] = useState("");
  const [editingMessage, setEditingMessage] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [detailsDraft, setDetailsDraft] = useState({ invoice_date: "", payment_terms: "" });

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(prefs));
  }, [prefs]);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const [invRes, sRes] = await Promise.all([
        api.get(`/invoices/${id}`),
        api.get("/settings"),
      ]);
      const inv = invRes.data;
      setInvoice(inv);
      setSettings(sRes.data);
      // Merge server-side customer-view defaults with any per-user localStorage overrides
      setPrefs(mergePrefs(sRes.data?.invoice_customer_view, loadLocalOverrides()));
      setDetailsDraft({
        invoice_date: (inv.created_at || "").slice(0, 10),
        payment_terms: inv.payment_terms || sRes.data?.default_payment_terms || "C.O.D.",
      });
      // Compose default invoice message from settings
      const hstLine = sRes.data?.hst_number ? `HST: ${sRes.data.hst_number}\n\n` : "";
      const footer = (sRes.data?.invoice_footer_message || "").trim();
      const longTerms = (sRes.data?.invoice_terms_long || "").trim();
      setInvoiceMessage(hstLine + (footer ? footer + "\n\n" : "") + longTerms);
      if (inv.customer_id) {
        const cRes = await api.get(`/customers/${inv.customer_id}`);
        setCustomer(cRes.data);
      }
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Round-3 bugfix (Feb 2026): line items store `rate` (not `unit_price`) and
  // don't have a `total` field — amount is computed from qty × rate at render
  // time. Preview previously read `li.unit_price` and `li.total`, both undefined,
  // making all line prices show as $0.00. This helper mirrors the PDF renderer.
  const lineUnitPrice = (li) => Number(li.rate ?? li.unit_price ?? 0);
  const lineAmount = (li) => {
    const sub = Number(li.quantity || 0) * lineUnitPrice(li);
    const ldt = (li.line_discount_type || "amount").toLowerCase();
    const ldv = Number(li.line_discount_value || 0);
    const total = ldt === "percent" ? sub * (1 - ldv / 100) : sub - ldv;
    return Math.max(total, 0);
  };

  const services = useMemo(() => {
    const items = invoice?.line_items || [];
    return items.filter((li) => (li.category || "service") !== "material");
  }, [invoice]);
  const materials = useMemo(() => {
    const items = invoice?.line_items || [];
    return items.filter((li) => li.category === "material");
  }, [invoice]);

  const servicesSubtotal = useMemo(
    () => services.reduce((s, li) => s + lineAmount(li), 0),
    [services]
  );
  const materialsSubtotal = useMemo(
    () => materials.reduce((s, li) => s + lineAmount(li), 0),
    [materials]
  );

  const balanceDue = useMemo(() => {
    if (!invoice) return 0;
    const total = Number(invoice.total || 0);
    const paid = (invoice.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
    return Math.max(total - paid, 0);
  }, [invoice]);

  const close = () => {
    // If arrived from job — go back there; else invoices list
    if (invoice?.job_id) nav(`/jobs/${invoice.job_id}`);
    else nav("/invoices");
  };

  const doSend = async () => {
    if (!customer?.email) {
      setErr("Customer has no email on file — add one before sending.");
      return;
    }
    setSending(true); setErr("");
    try {
      await api.post(`/invoices/${id}/send`, {
        to: customer.email, attach_pdf: true, template_key: "invoice",
      });
      // Return to previous page
      close();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setSending(false);
    }
  };

  const doPrint = () => {
    // Open the PDF in a new tab which the user can then print
    window.open(fileUrl(`/invoices/${id}/pdf`), "_blank", "noopener,noreferrer");
  };

  const doDownload = () => {
    // Trigger download of the PDF
    const url = fileUrl(`/invoices/${id}/pdf?download=1`);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 dark:text-slate-400">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading preview…
      </div>
    );
  }
  if (!invoice) {
    return <div className="p-8 text-sm text-red-600">Invoice not found.</div>;
  }

  const businessName = settings?.business_name || "Your Business";
  const businessAddr = settings?.address || "";
  const businessPhone = settings?.phone || "";
  const businessEmail = settings?.email || "";
  // Part 22 (Feb 2026): Service Address block visibility is controlled by
  // the `invoice_show_service_address` setting (default true).
  const showServiceAddress = settings?.invoice_show_service_address !== false;
  const custDisplay = customer?.display_name || customer?.name || "";
  const custCompany = customer?.company_name || "";
  const custEmail = customer?.email || "";
  const serviceAddr = invoice.service_address || customer?.billing_address || "";
  const invoiceNumber = invoice.number || invoice.invoice_number || "—";
  const jobNumberDisplay = invoice.job_number || invoiceNumber;
  const serviceDate = (invoice.service_date || invoice.created_at || "").slice(0, 10);
  const invoiceDateDisplay = detailsDraft.invoice_date || (invoice.created_at || "").slice(0, 10);
  const paymentTermsDisplay = detailsDraft.payment_terms || invoice.payment_terms || "Upon receipt";
  const dueDate = (invoice.due_date || "").slice(0, 10) || invoiceDateDisplay;
  const hstRate = Number(invoice.hst_rate ?? settings?.hst_rate ?? 13);
  const totalTax = Number(invoice.hst || 0);
  const subtotal = Number(invoice.subtotal || 0);
  const total = Number(invoice.total || 0);

  const brandColor = settings?.brand_color || "#0F4C81";

  const setField = (k, v) => setPrefs((p) => ({ ...p, fields: { ...p.fields, [k]: v } }));
  const setSection = (k, v) => setPrefs((p) => ({ ...p, sections: { ...p.sections, [k]: v } }));

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      {/* Top bar */}
      <header data-testid="send-invoice-header" className="sticky top-0 z-30 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-4 px-6 py-4">
          <button
            data-testid="send-invoice-close"
            onClick={close}
            className="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-300"
            title="Cancel"
          >
            <X size={22} />
          </button>
          <h1 className="font-display text-2xl font-bold flex-1">Send invoice</h1>
          <button
            data-testid="send-invoice-print-btn"
            onClick={doPrint}
            className="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-300"
            title="Print"
          >
            <Printer size={20} />
          </button>
          <button
            data-testid="send-invoice-download-btn"
            onClick={doDownload}
            className="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-300"
            title="Download PDF"
          >
            <Download size={20} />
          </button>
          <button
            data-testid="send-invoice-confirm-btn"
            onClick={doSend}
            disabled={sending || !customer?.email}
            className="px-6 py-2.5 rounded-full bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
          >
            {sending ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <>Send</>}
          </button>
        </div>
        {err && (
          <div data-testid="send-invoice-error" className="px-6 pb-3 text-sm text-red-600 dark:text-red-400">{err}</div>
        )}
      </header>

      <div className="max-w-[1600px] mx-auto grid lg:grid-cols-[360px_1fr] gap-6 p-6">
        {/* Left sidebar */}
        <aside className="space-y-4">
          {/* Details card */}
          <SidebarCard
            title="Details"
            testId="preview-details-card"
            headerAction={
              <button
                data-testid="preview-edit-details"
                onClick={() => setEditingDetails((v) => !v)}
                className="p-1 text-[#0F4C81] hover:text-[#0A365C]"
                title="Edit"
              >
                <Edit2 size={16} />
              </button>
            }
          >
            <div className="space-y-3">
              <Field label="Invoice #" value={invoiceNumber} />
              <Field
                label="Invoice date"
                value={
                  editingDetails ? (
                    <input
                      type="date"
                      value={detailsDraft.invoice_date}
                      onChange={(e) => setDetailsDraft((d) => ({ ...d, invoice_date: e.target.value }))}
                      className="w-full px-2 py-1 border border-slate-300 dark:border-slate-700 rounded text-sm bg-white dark:bg-slate-900"
                    />
                  ) : formatDateShort(invoiceDateDisplay)
                }
              />
              <Field
                label="Invoice due"
                value={
                  editingDetails ? (
                    <select
                      value={detailsDraft.payment_terms}
                      onChange={(e) => setDetailsDraft((d) => ({ ...d, payment_terms: e.target.value }))}
                      className="w-full px-2 py-1 border border-slate-300 dark:border-slate-700 rounded text-sm bg-white dark:bg-slate-900"
                    >
                      {["C.O.D.", "Due on Receipt", "Net 15", "Net 30", "Net 60"].map((t) => (
                        <option key={t}>{t}</option>
                      ))}
                    </select>
                  ) : paymentTermsDisplay
                }
              />
              <div className="pt-2 border-t border-slate-200 dark:border-slate-800 flex items-baseline justify-between">
                <div className="text-xs font-medium text-slate-500 dark:text-slate-400">Amount due</div>
                <div className="font-display text-2xl font-bold tabular" style={{ color: brandColor }}>{fmtCAD(balanceDue)}</div>
              </div>
            </div>
          </SidebarCard>

          {/* Attachments card */}
          <SidebarCard
            title="Attachments"
            testId="preview-attachments-card"
            headerAction={<button className="p-1 text-slate-400" title="Edit"><Edit2 size={16} /></button>}
          >
            <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-2">
              <Paperclip size={12} /> Invoice PDF will be attached automatically
            </div>
          </SidebarCard>

          {/* Invoice message card */}
          <SidebarCard
            title="Invoice message"
            testId="preview-message-card"
            headerAction={
              <button
                data-testid="preview-edit-message"
                onClick={() => setEditingMessage((v) => !v)}
                className="p-1 text-[#0F4C81] hover:text-[#0A365C]"
                title="Edit"
              >
                <Edit2 size={16} />
              </button>
            }
          >
            {editingMessage ? (
              <textarea
                data-testid="preview-message-input"
                value={invoiceMessage}
                onChange={(e) => setInvoiceMessage(e.target.value)}
                rows={8}
                className="w-full text-sm px-2 py-2 border border-slate-300 dark:border-slate-700 rounded bg-white dark:bg-slate-900"
              />
            ) : (
              <ExpandableText text={invoiceMessage} lines={7} />
            )}
          </SidebarCard>

          {/* Payment options */}
          <CollapsibleCard
            title="Payment options"
            open={prefs.sections.payment}
            onToggle={() => setSection("payment", !prefs.sections.payment)}
            testId="preview-payment-card"
          >
            <div className="bg-sky-50 dark:bg-sky-950/40 border border-sky-200 dark:border-sky-900 rounded p-3 text-xs text-slate-700 dark:text-slate-300 flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-sky-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">i</div>
              <p>To offer online payment options to your customers, connect your bank account.</p>
            </div>
            <button className="mt-3 px-4 py-1.5 border border-[#0F4C81] text-[#0F4C81] rounded-full text-sm font-medium hover:bg-[#0F4C81]/5" data-testid="preview-connect-bank">
              Connect my bank
            </button>
          </CollapsibleCard>

          {/* Job and invoice */}
          <CollapsibleCard
            title="Job and invoice"
            open={prefs.sections.jobAndInvoice}
            onToggle={() => setSection("jobAndInvoice", !prefs.sections.jobAndInvoice)}
            testId="preview-jobinvoice-card"
          >
            <Check label="Job number" k="jobNumber" prefs={prefs} setField={setField} />
            <Check label="Invoice number" k="invoiceNumber" prefs={prefs} setField={setField} />
            <Check label="Service date" k="serviceDate" prefs={prefs} setField={setField} />
            <Check label="Invoice date" k="invoiceDate" prefs={prefs} setField={setField} />
            <Check label="Summary of work" k="summaryOfWork" prefs={prefs} setField={setField} />
          </CollapsibleCard>

          {/* View format */}
          <CollapsibleCard
            title="View format"
            open={prefs.sections.viewFormat}
            onToggle={() => setSection("viewFormat", !prefs.sections.viewFormat)}
            testId="preview-viewformat-card"
          >
            <Radio
              name="viewFormat"
              value="email"
              checked={prefs.viewFormat === "email"}
              onChange={() => setPrefs((p) => ({ ...p, viewFormat: "email" }))}
              label="Email optimized"
            />
            <Radio
              name="viewFormat"
              value="envelope"
              checked={prefs.viewFormat === "envelope"}
              onChange={() => setPrefs((p) => ({ ...p, viewFormat: "envelope" }))}
              label="Envelope optimized"
              sub="For use with #9 or #10 window envelopes"
            />
          </CollapsibleCard>

          {/* Business and customer */}
          <CollapsibleCard
            title="Business and customer"
            open={prefs.sections.businessAndCustomer}
            onToggle={() => setSection("businessAndCustomer", !prefs.sections.businessAndCustomer)}
            testId="preview-bizcust-card"
          >
            <Check label="Business name" k="businessName" prefs={prefs} setField={setField} />
            <Check label="Technician name" k="technicianName" prefs={prefs} setField={setField} />
            <Check label="Customer display name" k="customerDisplayName" prefs={prefs} setField={setField} />
            <Check label="Customer company name" k="customerCompanyName" prefs={prefs} setField={setField} />
          </CollapsibleCard>

          {/* Services */}
          <CollapsibleCard
            title="Services"
            open={prefs.sections.services}
            onToggle={() => setSection("services", !prefs.sections.services)}
            testId="preview-services-card"
          >
            <Check label="Line items" k="servicesLineItems" prefs={prefs} setField={setField} />
            <Check label="Services name" k="servicesName" prefs={prefs} setField={setField} />
            <Check label="Description" k="servicesDescription" prefs={prefs} setField={setField} />
            <Check label="Quantity" k="servicesQuantity" prefs={prefs} setField={setField} />
            <Check label="Unit price" k="servicesUnitPrice" prefs={prefs} setField={setField} />
            <Check label="Line items amount" k="servicesAmount" prefs={prefs} setField={setField} />
            <Check label="Subtotal" k="servicesSubtotal" prefs={prefs} setField={setField} />
          </CollapsibleCard>

          {/* Materials */}
          <CollapsibleCard
            title="Materials"
            open={prefs.sections.materials}
            onToggle={() => setSection("materials", !prefs.sections.materials)}
            testId="preview-materials-card"
          >
            <Check label="Line items" k="materialsLineItems" prefs={prefs} setField={setField} />
            <Check label="Subtotal" k="materialsSubtotal" prefs={prefs} setField={setField} />
          </CollapsibleCard>
        </aside>

        {/* Right — invoice preview */}
        <main data-testid="invoice-preview-canvas" className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md shadow-sm p-10 ${prefs.viewFormat === "envelope" ? "envelope-format" : ""}`}>
          {/* Header row */}
          <div className="flex items-start justify-between gap-8">
            <div className="flex-1">
              {settings?.logo_path ? (
                <img src={fileUrl(settings.logo_path)} alt="" className="h-16 mb-3" />
              ) : (
                <div className="w-16 h-16 border border-slate-300 dark:border-slate-700 rounded flex items-center justify-center text-slate-400 dark:text-slate-500 mb-3">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="16" rx="1"/><circle cx="9" cy="10" r="1.5"/><path d="M4 18l5-6 4 4 3-3 5 6"/></svg>
                </div>
              )}
              {prefs.fields.businessName && (
                <div className="font-display text-xl font-bold" data-testid="preview-business-name">{businessName}</div>
              )}
              {/* Part 22 (Feb 2026): Contact Us directly under company name */}
              {(businessAddr || businessPhone || businessEmail) && (
                <div className="mt-2 text-sm text-slate-600 dark:text-slate-400 space-y-0.5" data-testid="preview-contact-us">
                  {businessAddr && <div className="whitespace-pre-wrap">{businessAddr}</div>}
                  {businessPhone && <div className="flex items-center gap-2"><Phone size={12} /> {businessPhone}</div>}
                  {businessEmail && <div className="flex items-center gap-2"><Mail size={12} /> {businessEmail}</div>}
                </div>
              )}
            </div>

            {/* Right meta box — Part 22: tinted background blocks */}
            <div className="w-[300px] border border-slate-200 dark:border-slate-800 rounded overflow-hidden shrink-0" data-testid="preview-meta-panel">
              <div className="bg-slate-50 dark:bg-slate-800/40">
                {prefs.fields.jobNumber && (
                  <MetaRow label="JOB" value={`#${jobNumberDisplay}`} testId="preview-job-number" />
                )}
                {prefs.fields.invoiceNumber && (
                  <MetaRow label="INVOICE" value={`#${invoiceNumber}`} testId="preview-invoice-number" />
                )}
                {prefs.fields.serviceDate && (
                  <MetaRow label="SERVICE DATE" value={formatDateLong(serviceDate)} testId="preview-service-date" />
                )}
                {prefs.fields.invoiceDate && (
                  <MetaRow label="INVOICE DATE" value={formatDateLong(invoiceDateDisplay)} testId="preview-invoice-date" />
                )}
                <MetaRow label="PAYMENT TERMS" value={paymentTermsDisplay} />
                <MetaRow label="DUE DATE" value={formatDateLong(dueDate)} />
              </div>
              <div className="px-4 py-3 bg-blue-50 dark:bg-blue-950/40 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between" data-testid="preview-amount-due-row">
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">AMOUNT DUE</span>
                <span className="font-display text-lg font-bold tabular" style={{ color: brandColor }}>{fmtCAD(balanceDue)}</span>
              </div>
            </div>
          </div>

          {/* Customer address row.
              (Aug 2026) SERVICE ADDRESS block removed from the customer-facing
              invoice per user request. The service_address field on the
              job/invoice document is preserved — only its display here is
              removed. */}
          <div className="mt-10 grid grid-cols-2 gap-8">
            <div className="text-sm space-y-1">
              {prefs.fields.customerCompanyName && custCompany && (
                <div className="font-semibold" data-testid="preview-cust-company">{custCompany}</div>
              )}
              {prefs.fields.customerDisplayName && (
                <div className="font-semibold" data-testid="preview-cust-name">{custDisplay}</div>
              )}
              <div className="text-slate-600 dark:text-slate-400 whitespace-pre-wrap">{customer?.billing_address || ""}</div>
              {custEmail && (
                <div className="mt-3 flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <Mail size={13} /> {custEmail}
                </div>
              )}
              {/* Part 25 (Feb 2026): Total Owed / YTD Paid removed from Send Preview.
                  It lives ONLY on the "Made for you" panel on JobDetail — nowhere else,
                  and never on any customer-facing surface. Do not re-add here. */}
            </div>
            <div className="text-sm space-y-3" />
          </div>

          {/* INVOICE section */}
          <div className="mt-10">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-3">Invoice</div>

            {/* Services table */}
            {prefs.fields.servicesLineItems && services.length > 0 && (
              <div data-testid="preview-services-table" className="mb-6">
                <div className="bg-slate-100 dark:bg-slate-800 px-3 py-2 grid grid-cols-[1fr_100px_120px] gap-3 text-[11px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                  <div>Services</div>
                  {prefs.fields.servicesUnitPrice && <div className="text-right">unit price</div>}
                  {prefs.fields.servicesAmount && <div className="text-right">amount</div>}
                </div>
                {services.map((li, i) => (
                  <div key={li.id ?? `svc-${li.description ?? li.name ?? "row"}-${i}`} className="border-b border-slate-100 dark:border-slate-800 py-3 grid grid-cols-[1fr_100px_120px] gap-3 text-sm">
                    <div>
                      {prefs.fields.servicesName && (
                        <div className="font-medium">{li.name || li.description || "—"}</div>
                      )}
                      {prefs.fields.servicesDescription && li.description && li.name && (
                        <div className="text-slate-500 dark:text-slate-400 text-[13px] mt-1 whitespace-pre-wrap">{li.description}</div>
                      )}
                      {prefs.fields.servicesQuantity && (
                        <div className="text-slate-500 dark:text-slate-400 text-xs mt-1">Qty: {li.quantity}</div>
                      )}
                    </div>
                    {prefs.fields.servicesUnitPrice && <div className="text-right tabular">{fmtCAD(lineUnitPrice(li))}</div>}
                    {prefs.fields.servicesAmount && <div className="text-right tabular font-medium">{fmtCAD(lineAmount(li))}</div>}
                  </div>
                ))}
                {prefs.fields.servicesSubtotal && (
                  <div className="text-right text-sm mt-2 text-slate-600 dark:text-slate-300">
                    Services subtotal: <span className="font-semibold tabular ml-1">{fmtCAD(servicesSubtotal)}</span>
                  </div>
                )}
              </div>
            )}

            {/* Materials table */}
            {prefs.fields.materialsLineItems && materials.length > 0 && (
              <div data-testid="preview-materials-table" className="mb-6">
                <div className="bg-slate-100 dark:bg-slate-800 px-3 py-2 grid grid-cols-[1fr_100px_120px] gap-3 text-[11px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                  <div>Materials</div>
                  <div className="text-right">unit price</div>
                  <div className="text-right">amount</div>
                </div>
                {materials.map((li, i) => (
                  <div key={li.id ?? `mat-${li.description ?? li.name ?? "row"}-${i}`} className="border-b border-slate-100 dark:border-slate-800 py-3 grid grid-cols-[1fr_100px_120px] gap-3 text-sm">
                    <div>
                      <div className="font-medium">{li.name || li.description || "—"}</div>
                      {li.description && li.name && (
                        <div className="text-slate-500 dark:text-slate-400 text-[13px] mt-1 whitespace-pre-wrap">{li.description}</div>
                      )}
                    </div>
                    <div className="text-right tabular">{fmtCAD(lineUnitPrice(li))}</div>
                    <div className="text-right tabular font-medium">{fmtCAD(lineAmount(li))}</div>
                  </div>
                ))}
                {prefs.fields.materialsSubtotal && (
                  <div className="text-right text-sm mt-2 text-slate-600 dark:text-slate-300">
                    Materials subtotal: <span className="font-semibold tabular ml-1">{fmtCAD(materialsSubtotal)}</span>
                  </div>
                )}
              </div>
            )}

            {/* Totals block */}
            <div className="flex justify-end mt-8">
              <div className="w-[320px] space-y-2">
                <TotalRow label="Subtotal" value={fmtCAD(subtotal)} />
                <TotalRow label="Total Tax" value={fmtCAD(totalTax)} />
                <TotalRow label={`HST (${hstRate}%)`} value={fmtCAD(totalTax)} muted />
                <TotalRow label="Job Total" value={fmtCAD(total)} />
                <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex items-baseline justify-between">
                  <span className="font-semibold">Amount Due</span>
                  <span className="font-display text-xl font-bold tabular" style={{ color: brandColor }}>{fmtCAD(balanceDue)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Message / Terms */}
          {invoiceMessage && (
            <div className="mt-12 pt-6 border-t border-slate-200 dark:border-slate-800 text-[13px] leading-relaxed text-slate-700 dark:text-slate-300 whitespace-pre-wrap" data-testid="preview-message">
              {invoiceMessage}
            </div>
          )}

          {/* Terms link */}
          {settings?.invoice_terms_url && (
            <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-800 text-sm text-slate-600 dark:text-slate-400">
              See our <a href={settings.invoice_terms_url} target="_blank" rel="noopener noreferrer" className="text-[#0F4C81] hover:underline font-medium">{settings.invoice_terms_text || "Terms & Conditions"}</a>
            </div>
          )}

          {/* Footer */}
          <div className="mt-10 pt-4 border-t border-slate-200 dark:border-slate-800 text-xs text-slate-400 dark:text-slate-500">
            {businessName}
          </div>
        </main>
      </div>
    </div>
  );
}

/* ------------ helpers / atoms ------------- */

function formatDateLong(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString("en-CA", {
      day: "numeric", month: "short", year: "numeric",
    }).replace(",", ",");
  } catch { return iso; }
}
function formatDateShort(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
    return d.toLocaleDateString("en-CA", { month: "2-digit", day: "2-digit", year: "numeric" });
  } catch { return iso; }
}

function SidebarCard({ title, testId, children, headerAction }) {
  return (
    <div data-testid={testId} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold text-slate-900 dark:text-slate-100">{title}</h3>
        {headerAction}
      </div>
      {children}
    </div>
  );
}

function CollapsibleCard({ title, open, onToggle, testId, children }) {
  return (
    <div data-testid={testId} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-4">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left"
        aria-expanded={open}
      >
        <h3 className="font-display font-bold text-slate-900 dark:text-slate-100">{title}</h3>
        {open ? <ChevronUp size={18} className="text-slate-500" /> : <ChevronDown size={18} className="text-slate-500" />}
      </button>
      {open && <div className="mt-3 space-y-2.5">{children}</div>}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium mb-0.5">{label}</div>
      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  );
}

function MetaRow({ label, value, testId }) {
  return (
    <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-800 last:border-b-0 flex items-center justify-between text-sm">
      <span className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-medium">{label}</span>
      <span data-testid={testId} className="tabular font-medium">{value}</span>
    </div>
  );
}

function TotalRow({ label, value, muted }) {
  return (
    <div className={`flex items-center justify-between text-sm ${muted ? "text-slate-500 dark:text-slate-400" : "text-slate-800 dark:text-slate-200"}`}>
      <span className={muted ? "pl-4" : ""}>{label}</span>
      <span className="tabular">{value}</span>
    </div>
  );
}

function Check({ label, k, prefs, setField }) {
  const checked = !!prefs.fields[k];
  return (
    <label className="flex items-center gap-2.5 cursor-pointer text-sm text-slate-700 dark:text-slate-300 select-none">
      <input
        type="checkbox"
        data-testid={`preview-toggle-${k}`}
        checked={checked}
        onChange={(e) => setField(k, e.target.checked)}
        className="w-4 h-4 rounded accent-[#0F4C81]"
      />
      {label}
    </label>
  );
}

function Radio({ name, value, checked, onChange, label, sub }) {
  return (
    <label className="flex items-start gap-2.5 cursor-pointer text-sm text-slate-700 dark:text-slate-300 select-none">
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className="mt-0.5 w-4 h-4 accent-[#0F4C81]"
      />
      <div>
        <div>{label}</div>
        {sub && <div className="text-xs text-slate-500 dark:text-slate-400">{sub}</div>}
      </div>
    </label>
  );
}

function ExpandableText({ text, lines }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) {
    return <div className="text-xs text-slate-400 italic">No message set</div>;
  }
  return (
    <div className="text-sm text-slate-700 dark:text-slate-300">
      <div className={expanded ? "whitespace-pre-wrap" : "whitespace-pre-wrap"}
           style={expanded ? {} : { display: "-webkit-box", WebkitLineClamp: lines, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
        {text}
      </div>
      <button
        onClick={() => setExpanded((v) => !v)}
        data-testid="preview-message-toggle"
        className="mt-2 text-[#0F4C81] hover:text-[#0A365C] text-sm font-medium flex items-center gap-1"
      >
        Show {expanded ? "less" : "more"} {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
    </div>
  );
}
